import { useEffect, useState, useRef } from "react";
import api, { API } from "@/lib/api";
import { useBilling } from "@/context/BillingContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  FileText,
  Download,
  Send,
  Link2,
  Trash2,
  X,
  Loader2,
  Sparkles,
  CheckCircle2,
  Circle,
  DollarSign,
  AlertTriangle,
  Building2,
  ImagePlus,
  Bell,
} from "lucide-react";

const statusMeta = {
  draft: { label: "Draft", className: "bg-muted text-muted-foreground" },
  sent: { label: "Sent", className: "bg-blue-100 text-blue-700" },
  paid: { label: "Paid", className: "bg-emerald-100 text-emerald-700" },
};

const blank = {
  property_id: "",
  homeowner_name: "",
  homeowner_email: "",
  period_start: "",
  period_end: "",
  due_date: "",
  line_items: [],
  tax_rate: "",
  late_fee: "",
  auto_late_fee: false,
  auto_reminders: true,
  notes: "",
};

export default function Invoices() {
  const { isPremium } = useBilling();
  const [invoices, setInvoices] = useState([]);
  const [properties, setProperties] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [busy, setBusy] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bizOpen, setBizOpen] = useState(false);
  const [company, setCompany] = useState({ name: "", address: "", phone: "", logo_url: null });
  const [savingBiz, setSavingBiz] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);

  const load = () => api.get("/invoices").then((r) => setInvoices(r.data));
  useEffect(() => {
    load();
    api.get("/properties").then((r) => setProperties(r.data));
    api.get("/company").then((r) => setCompany(r.data)).catch(() => {});
  }, []);

  const saveBiz = async () => {
    setSavingBiz(true);
    try {
      const { data } = await api.put("/company", {
        name: company.name,
        address: company.address,
        phone: company.phone,
      });
      setCompany(data);
      toast.success("Business details saved — they'll appear on your invoices");
      setBizOpen(false);
    } catch (e) {
      toast.error("Could not save business details");
    } finally {
      setSavingBiz(false);
    }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/company/logo", fd);
      setCompany((c) => ({ ...c, logo_url: data.logo_url, logo_path: data.logo_path }));
      toast.success("Logo uploaded");
    } catch (e) {
      toast.error("Could not upload logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  const openNew = () => {
    setForm(blank);
    setOpen(true);
  };

  const runReminders = async () => {
    setBusy("reminders");
    try {
      const { data } = await api.post("/invoices/run-reminders");
      toast.success(data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send reminders");
    } finally {
      setBusy("");
    }
  };

  const onSelectProperty = (pid) => {
    const p = properties.find((x) => x.id === pid);
    setForm((f) => ({
      ...f,
      property_id: pid,
      homeowner_name: p?.homeowner_name || "",
      homeowner_email: p?.homeowner_email || "",
    }));
  };

  const pullCleanings = async () => {
    if (!form.property_id) return toast.error("Select a property first");
    setGenerating(true);
    try {
      const { data } = await api.post("/invoices/generate", {
        property_id: form.property_id,
        period_start: form.period_start || null,
        period_end: form.period_end || null,
      });
      setForm((f) => ({
        ...f,
        homeowner_name: f.homeowner_name || data.homeowner_name,
        homeowner_email: f.homeowner_email || data.homeowner_email,
        line_items: data.line_items.length
          ? data.line_items
          : f.line_items,
      }));
      if (!data.line_items.length)
        toast.info("No completed cleanings found in that range");
      else toast.success(`Added ${data.line_items.length} cleaning(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not pull cleanings");
    } finally {
      setGenerating(false);
    }
  };

  const addLine = () =>
    setForm((f) => ({
      ...f,
      line_items: [...f.line_items, { description: "", quantity: 1, unit_price: 0 }],
    }));

  const updateLine = (i, key, value) =>
    setForm((f) => ({
      ...f,
      line_items: f.line_items.map((li, idx) =>
        idx === i ? { ...li, [key]: value } : li
      ),
    }));

  const removeLine = (i) =>
    setForm((f) => ({
      ...f,
      line_items: f.line_items.filter((_, idx) => idx !== i),
    }));

  const subtotal = form.line_items.reduce(
    (s, li) => s + (parseFloat(li.quantity) || 0) * (parseFloat(li.unit_price) || 0),
    0
  );
  const tax = subtotal * (parseFloat(form.tax_rate) || 0) / 100;
  const total = subtotal + tax;

  const save = async () => {
    if (!form.property_id) return toast.error("Select a property");
    if (!form.line_items.length) return toast.error("Add at least one line item");
    setSaving(true);
    try {
      await api.post("/invoices", {
        ...form,
        period_start: form.period_start || null,
        period_end: form.period_end || null,
        due_date: form.due_date || null,
        tax_rate: parseFloat(form.tax_rate) || 0,
        late_fee: parseFloat(form.late_fee) || 0,
        auto_late_fee: !!form.auto_late_fee,
        auto_reminders: !!form.auto_reminders,
        line_items: form.line_items.map((li) => ({
          description: li.description,
          quantity: parseFloat(li.quantity) || 0,
          unit_price: parseFloat(li.unit_price) || 0,
        })),
      });
      toast.success("Invoice created");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create invoice");
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async (inv) => {
    setBusy(`pdf-${inv.id}`);
    try {
      const res = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${inv.number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download PDF");
    } finally {
      setBusy("");
    }
  };

  const sendEmail = async (inv) => {
    setBusy(`send-${inv.id}`);
    try {
      const { data } = await api.post(`/invoices/${inv.id}/send`, {
        origin_url: window.location.origin,
      });
      toast.success(data.message || "Invoice sent");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send invoice");
    } finally {
      setBusy("");
    }
  };

  const copyPayLink = async (inv) => {
    try {
      let token = inv.pay_token;
      if (!token) {
        const { data } = await api.get(`/invoices/${inv.id}/pay-link`);
        token = data.pay_token;
      }
      const url = `${window.location.origin}/invoice/${token}`;
      await navigator.clipboard.writeText(url);
      toast.success("Payment link copied to clipboard");
    } catch (e) {
      toast.error("Could not copy payment link");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/invoices/${id}`);
      toast.success("Invoice deleted");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error("Could not delete invoice");
    }
  };

  const setStatus = async (inv, status) => {
    setBusy(`status-${inv.id}`);
    try {
      await api.put(`/invoices/${inv.id}/status`, { status });
      toast.success(status === "paid" ? "Marked as paid" : "Marked as unpaid");
      load();
    } catch (e) {
      toast.error("Could not update status");
    } finally {
      setBusy("");
    }
  };

  const toggleLateFee = async (inv, apply) => {
    setBusy(`fee-${inv.id}`);
    try {
      await api.put(`/invoices/${inv.id}/late-fee`, { apply });
      toast.success(apply ? "Late fee applied" : "Late fee removed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update late fee");
    } finally {
      setBusy("");
    }
  };

  const totalInvoiced = invoices.reduce((s, i) => s + i.total, 0);
  const totalPaid = invoices.filter((i) => i.status === "paid").reduce((s, i) => s + i.total, 0);
  const outstanding = totalInvoiced - totalPaid;

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="text-xs tracking-[0.15em] uppercase font-bold text-primary mb-1">
            Billing
          </p>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Invoices</h1>
        </div>
        <div className="flex gap-2">
          {isPremium && (
            <>
              <Button variant="outline" onClick={runReminders} disabled={busy === "reminders"} data-testid="run-reminders-button">
                {busy === "reminders" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Bell className="h-4 w-4 mr-2" />}
                Send reminders now
              </Button>
              <Button variant="outline" onClick={() => setBizOpen(true)} data-testid="business-details-button">
                <Building2 className="h-4 w-4 mr-2" /> Business details
              </Button>
            </>
          )}
          <Button onClick={openNew} data-testid="new-invoice-button">
            <Plus className="h-4 w-4 mr-2" /> New invoice
          </Button>
        </div>
      </div>

      {invoices.length === 0 ? (
        <Card className="p-12 text-center" data-testid="invoices-empty">
          <FileText className="h-10 w-10 text-muted-foreground mx-auto mb-4" strokeWidth={1.3} />
          <p className="text-muted-foreground">
            No invoices yet. Create one to bill a home owner for completed cleanings.
          </p>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-4 mb-6">
            <Card className="p-5" data-testid="stat-invoiced">
              <DollarSign className="h-5 w-5 text-primary mb-3" strokeWidth={1.7} />
              <div className="font-display text-2xl sm:text-3xl font-semibold">${totalInvoiced.toFixed(2)}</div>
              <p className="text-sm text-muted-foreground mt-1">Total invoiced</p>
            </Card>
            <Card className="p-5" data-testid="stat-paid">
              <CheckCircle2 className="h-5 w-5 text-emerald-600 mb-3" strokeWidth={1.7} />
              <div className="font-display text-2xl sm:text-3xl font-semibold">${totalPaid.toFixed(2)}</div>
              <p className="text-sm text-muted-foreground mt-1">Paid</p>
            </Card>
            <Card className="p-5 bg-primary text-primary-foreground" data-testid="stat-outstanding">
              <Circle className="h-5 w-5 mb-3 opacity-90" strokeWidth={1.7} />
              <div className="font-display text-2xl sm:text-3xl font-semibold">${outstanding.toFixed(2)}</div>
              <p className="text-sm opacity-80 mt-1">Outstanding</p>
            </Card>
          </div>
          <div className="space-y-3">
          {invoices.map((inv) => {
            const meta = statusMeta[inv.status] || statusMeta.draft;
            return (
              <Card
                key={inv.id}
                className={`p-5 flex flex-col sm:flex-row sm:items-center gap-4 ${
                  inv.overdue ? "border-l-4 border-l-rose-500" : ""
                }`}
                data-testid={`invoice-row-${inv.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display font-semibold">{inv.number}</span>
                    <Badge className={`${meta.className} border-0`}>{meta.label}</Badge>
                    {inv.overdue && (
                      <Badge className="bg-rose-100 text-rose-700 border-0 gap-1" data-testid={`overdue-badge-${inv.id}`}>
                        <AlertTriangle className="h-3 w-3" /> Overdue
                      </Badge>
                    )}
                    {inv.late_fee_applied && (
                      <Badge className="bg-amber-100 text-amber-700 border-0">
                        + ${Number(inv.late_fee).toFixed(2)} late fee
                      </Badge>
                    )}
                    {inv.auto_late_fee && !inv.late_fee_applied && inv.late_fee > 0 && inv.status !== "paid" && (
                      <Badge variant="outline" className="gap-1 text-muted-foreground">
                        <AlertTriangle className="h-3 w-3" /> Auto fee ${Number(inv.late_fee).toFixed(2)}
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-0.5 truncate">
                    {inv.property_name} · {inv.homeowner_name || "No home owner"}
                    {inv.homeowner_email ? ` (${inv.homeowner_email})` : ""}
                  </p>
                  {inv.reminders && (
                    <p
                      className="text-xs text-muted-foreground mt-1 flex items-center gap-1"
                      data-testid={`reminder-info-${inv.id}`}
                    >
                      <Bell className="h-3 w-3" />
                      {inv.reminders.label
                        ? inv.reminders.label
                        : `${inv.reminders.last ? `Last reminder ${inv.reminders.last}` : "No reminders sent yet"}${inv.reminders.next ? ` · Next ${inv.reminders.next}` : ""}`}
                    </p>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <p className="font-display text-xl font-semibold">${inv.total.toFixed(2)}</p>
                  {inv.due_date && (
                    <p className={`text-xs ${inv.overdue ? "text-rose-600 font-medium" : "text-muted-foreground"}`}>
                      Due {inv.due_date}
                    </p>
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button
                    variant={inv.status === "paid" ? "outline" : "default"}
                    size="sm"
                    onClick={() => setStatus(inv, inv.status === "paid" ? "sent" : "paid")}
                    disabled={busy === `status-${inv.id}`}
                    data-testid={`toggle-paid-${inv.id}`}
                  >
                    {busy === `status-${inv.id}` ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : inv.status === "paid" ? (
                      <Circle className="h-4 w-4" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4" />
                    )}
                    <span className="ml-1 hidden sm:inline">
                      {inv.status === "paid" ? "Unpaid" : "Mark paid"}
                    </span>
                  </Button>
                  {inv.late_fee > 0 && inv.status !== "paid" && (
                    <Button
                      variant={inv.late_fee_applied ? "outline" : "secondary"}
                      size="sm"
                      onClick={() => toggleLateFee(inv, !inv.late_fee_applied)}
                      disabled={busy === `fee-${inv.id}`}
                      data-testid={`late-fee-${inv.id}`}
                    >
                      {busy === `fee-${inv.id}` ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <AlertTriangle className="h-4 w-4" />
                      )}
                      <span className="ml-1 hidden sm:inline">
                        {inv.late_fee_applied ? "Remove fee" : "Add late fee"}
                      </span>
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => downloadPdf(inv)}
                    disabled={busy === `pdf-${inv.id}`}
                    data-testid={`download-invoice-${inv.id}`}
                  >
                    {busy === `pdf-${inv.id}` ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => sendEmail(inv)}
                    disabled={busy === `send-${inv.id}` || !inv.homeowner_email}
                    data-testid={`send-invoice-${inv.id}`}
                  >
                    {busy === `send-${inv.id}` ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                    <span className="ml-1 hidden sm:inline">Send</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyPayLink(inv)}
                    title="Copy Pay Now link"
                    data-testid={`copy-paylink-${inv.id}`}
                  >
                    <Link2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDeleteTarget(inv)}
                    data-testid={`delete-invoice-${inv.id}`}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
        </>
      )}

      {/* New invoice dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto scrollbar-thin">
          <DialogHeader>
            <DialogTitle className="font-display">New invoice</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Property *</Label>
              <Select value={form.property_id} onValueChange={onSelectProperty}>
                <SelectTrigger data-testid="invoice-property-select">
                  <SelectValue placeholder="Select a property" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Home owner name</Label>
                <Input
                  value={form.homeowner_name}
                  onChange={(e) => setForm({ ...form, homeowner_name: e.target.value })}
                  data-testid="invoice-homeowner-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Home owner email</Label>
                <Input
                  type="email"
                  value={form.homeowner_email}
                  onChange={(e) => setForm({ ...form, homeowner_email: e.target.value })}
                  data-testid="invoice-homeowner-email-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Period start</Label>
                <Input
                  type="date"
                  value={form.period_start}
                  onChange={(e) => setForm({ ...form, period_start: e.target.value })}
                  data-testid="invoice-start-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Period end</Label>
                <Input
                  type="date"
                  value={form.period_end}
                  onChange={(e) => setForm({ ...form, period_end: e.target.value })}
                  data-testid="invoice-end-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Due date</Label>
                <Input
                  type="date"
                  value={form.due_date}
                  onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                  data-testid="invoice-due-input"
                />
                <p className="text-[11px] text-muted-foreground">Overdue after this date if unpaid</p>
              </div>
              <div className="space-y-2">
                <Label>Late fee ($)</Label>
                <Input
                  type="number"
                  value={form.late_fee}
                  onChange={(e) => setForm({ ...form, late_fee: e.target.value })}
                  placeholder="0"
                  data-testid="invoice-late-fee-input"
                />
                <p className="text-[11px] text-muted-foreground">Charged if the invoice goes overdue</p>
              </div>
            </div>

            {isPremium ? (
              <>
                <label className="flex items-center gap-2 cursor-pointer" data-testid="invoice-auto-fee-label">
                  <Checkbox
                    checked={form.auto_late_fee}
                    onCheckedChange={(v) => setForm({ ...form, auto_late_fee: !!v })}
                    data-testid="invoice-auto-fee-checkbox"
                  />
                  <span className="text-sm">
                    Automatically apply the late fee the moment this invoice becomes overdue
                    {" "}
                    <span className="text-muted-foreground">(notifies the home owner by email)</span>
                  </span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer" data-testid="invoice-auto-reminders-label">
                  <Checkbox
                    checked={form.auto_reminders}
                    onCheckedChange={(v) => setForm({ ...form, auto_reminders: !!v })}
                    data-testid="invoice-auto-reminders-checkbox"
                  />
                  <span className="text-sm">
                    Email automatic payment reminders (3 days before due, then every 3 days until paid)
                  </span>
                </label>
              </>
            ) : (
              <div
                className="rounded-lg border border-dashed border-border bg-muted/40 px-3 py-2.5 text-xs text-muted-foreground"
                data-testid="invoice-automation-upsell"
              >
                Automatic late fees &amp; payment reminders are a{" "}
                <span className="font-medium text-foreground">Premium</span> feature.
              </div>
            )}

            <Button
              type="button"
              variant="secondary"
              onClick={pullCleanings}
              disabled={generating}
              className="w-full"
              data-testid="pull-cleanings-button"
            >
              {generating ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4 mr-2" />
              )}
              Pull completed cleanings
            </Button>

            {/* Line items */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Line items</Label>
                <Button type="button" variant="ghost" size="sm" onClick={addLine} data-testid="add-line-button">
                  <Plus className="h-4 w-4 mr-1" /> Add line
                </Button>
              </div>
              {form.line_items.length === 0 && (
                <p className="text-sm text-muted-foreground py-2">
                  No items yet. Pull completed cleanings or add lines manually.
                </p>
              )}
              <div className="space-y-2">
                {form.line_items.map((li, i) => (
                  <div key={i} className="flex gap-2 items-center" data-testid={`line-item-${i}`}>
                    <Input
                      className="flex-1"
                      placeholder="Description"
                      value={li.description}
                      onChange={(e) => updateLine(i, "description", e.target.value)}
                      data-testid={`line-desc-${i}`}
                    />
                    <Input
                      className="w-16"
                      type="number"
                      placeholder="Qty"
                      value={li.quantity}
                      onChange={(e) => updateLine(i, "quantity", e.target.value)}
                      data-testid={`line-qty-${i}`}
                    />
                    <Input
                      className="w-24"
                      type="number"
                      placeholder="Price"
                      value={li.unit_price}
                      onChange={(e) => updateLine(i, "unit_price", e.target.value)}
                      data-testid={`line-price-${i}`}
                    />
                    <button onClick={() => removeLine(i)} type="button" aria-label="Remove line" data-testid={`remove-line-${i}`}>
                      <X className="h-4 w-4 text-muted-foreground" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Tax rate (%)</Label>
                <Input
                  type="number"
                  value={form.tax_rate}
                  onChange={(e) => setForm({ ...form, tax_rate: e.target.value })}
                  placeholder="0"
                  data-testid="invoice-tax-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Input
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Payment terms, thank you note..."
                  data-testid="invoice-notes-input"
                />
              </div>
            </div>

            <div className="rounded-lg bg-secondary p-4 space-y-1 text-sm" data-testid="invoice-totals">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subtotal</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Tax</span>
                <span>${tax.toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-display text-lg font-semibold pt-1">
                <span>Total</span>
                <span className="text-primary">${total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={save} disabled={saving} data-testid="save-invoice-button">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Business details dialog */}
      <Dialog open={bizOpen} onOpenChange={setBizOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">Business details</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2">
            These appear on your invoices and contracts.
          </p>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Logo</Label>
              <div className="flex items-center gap-3">
                <div className="h-16 w-16 rounded-lg border border-border flex items-center justify-center overflow-hidden bg-muted shrink-0">
                  {company.logo_url ? (
                    <img
                      src={`${API.replace(/\/api$/, "")}${company.logo_url}`}
                      alt="logo"
                      className="h-full w-full object-contain"
                      data-testid="company-logo-preview"
                    />
                  ) : (
                    <Building2 className="h-6 w-6 text-muted-foreground" />
                  )}
                </div>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={uploadLogo}
                  data-testid="company-logo-input"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => logoInputRef.current?.click()}
                  disabled={uploadingLogo}
                  data-testid="upload-logo-button"
                >
                  {uploadingLogo ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <ImagePlus className="h-4 w-4 mr-2" />
                  )}
                  Upload logo
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Business name</Label>
              <Input
                value={company.name || ""}
                onChange={(e) => setCompany({ ...company, name: e.target.value })}
                data-testid="company-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <Input
                value={company.address || ""}
                onChange={(e) => setCompany({ ...company, address: e.target.value })}
                placeholder="123 Clean St, Austin, TX 78701"
                data-testid="company-address-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={company.phone || ""}
                onChange={(e) => setCompany({ ...company, phone: e.target.value })}
                placeholder="(512) 555-0100"
                data-testid="company-phone-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBizOpen(false)}>Cancel</Button>
            <Button onClick={saveBiz} disabled={savingBiz} data-testid="save-business-button">
              {savingBiz && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete invoice {deleteTarget?.number}?</AlertDialogTitle>
            <AlertDialogDescription>
              This invoice will be permanently removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-invoice">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => remove(deleteTarget.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-invoice"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
